"use client";

import { type FC, useEffect } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import { Stack } from "@huub-web/ui";
import { getEndpointByPathFromNavigationState } from "@huub-web/ui/services/navigation";
// Direct import to avoid barrel export pulling in @jitsi/react-sdk during SSR
import { VideoCallController } from "@huub-web/advisor/components/VideoCallController";

import { AdvisorScreen } from "../AdvisorScreen/AdvisorScreen";
import { TemplatesScreen } from "../TemplatesScreen/TemplatesScreen";
import { HistoryScreen } from "../HistoryScreen/HistoryScreen";
import { InteriorDesignerScreen } from "../InteriorDesigner/InteriorDesignerScreen";
import { useNavigate, useAppController } from "../../hooks";
import type { PageOutletContext } from "../../hooks/usePageReady";
import type { ScreenType } from "../../utils/generateRoutesFromNavigationState";

const CALENDLY_URL =
  import.meta.env.VITE_CALENDLY_URL ??
  "https://calendly.com/d/cq33-cgp-jv2/huub-kennismaking";

type AppScreenProps = {
  /**
   * The type of screen to render
   */
  screenType: ScreenType;
  /**
   * The API endpoint to fetch templates from
   */
  endpoint?: string;
};

/**
 * Route-specific screen content. Renders the appropriate screen
 * (TemplatesScreen, AdvisorScreen, or HistoryScreen) based on the route config.
 * This component is rendered inside the animated Outlet of AppPageLayout.
 */
export const AppScreen: FC<AppScreenProps> = ({ screenType, endpoint }) => {
  const { navigationState, user, isAppStateLoading } = useAppController();
  const { location } = useNavigate();
  const { onLoadComplete } = useOutletContext<PageOutletContext>();
  const params = useParams();

  const dynamicID = params.id;

  /**
   * Resolves the endpoint by replacing placeholders like [id] with actual path values.
   * The endpoint prop may contain bracket notation (e.g., /frontend/house/[id])
   * which needs to be substituted with actual values from the current path.
   */
  const resolvedEndpoint = (() => {
    if (!endpoint || !navigationState || !location?.pathname) {
      return endpoint;
    }
    const resolved = getEndpointByPathFromNavigationState(
      location.pathname,
      navigationState,
    );
    if (resolved && location?.search) {
      return `${resolved}${resolved.includes("?") ? "&" : "?"}${location.search.slice(1)}`;
    }
    return resolved;
  })();

  const willRenderScreen =
    screenType === "advisor" ||
    screenType === "interior-designer" ||
    (screenType === "history" && !!dynamicID) ||
    (screenType === "templates" && !!resolvedEndpoint);

  /**
   * When no screen will be rendered (wrong variant, missing data),
   * signal ready immediately so the page fade-in isn't blocked.
   * Waits until the app state has loaded so we can distinguish between
   * "data not loaded yet" (temporary) and "genuinely nothing to render" (stable).
   */
  useEffect(() => {
    if (!willRenderScreen && !isAppStateLoading) {
      onLoadComplete();
    }
  }, [willRenderScreen, isAppStateLoading, onLoadComplete]);

  const renderScreen = () => {
    switch (screenType) {
      case "advisor":
        return <AdvisorScreen user={user} url={CALENDLY_URL} />;

      case "history":
        if (!dynamicID) {
          return null;
        }
        return <HistoryScreen key={dynamicID} chatID={dynamicID} />;

      case "interior-designer":
        return <InteriorDesignerScreen />;

      default:
        if (!resolvedEndpoint) {
          return null;
        }
        return <TemplatesScreen endpoint={resolvedEndpoint} />;
    }
  };

  return (
    <>
      <VideoCallController />
      <Stack data-stack="screens">{renderScreen()}</Stack>
    </>
  );
};
